// ============================================
// Keyboard Navigation Hook
// ============================================

import { useEffect } from 'preact/hooks';
import { KEYBOARD_SHORTCUTS } from '@/constants';
import {
  currentMode,
  quizAnswered,
  isFlipped,
  flipCard,
  nextCard,
  prevCard,
  gradeStudyCard,
  toggleKana
} from '@/state/signals';

interface UseKeyboardOptions {
  onQuizAnswer?: (index: number) => void;
  enabled?: boolean;
}

// Helper to check if key is in array (works with readonly arrays)
function keyIn(key: string, keys: readonly string[]): boolean {
  return keys.includes(key);
}

export function useKeyboard({ onQuizAnswer, enabled = true }: UseKeyboardOptions = {}) {
  // eslint-disable-next-line no-restricted-syntax
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        (e.target as HTMLElement).isContentEditable
      ) {
        return;
      }

      const { key } = e;

      // If a native control has focus (e.g. the Again / Got it grade buttons),
      // let Enter/Space activate it instead of hijacking those keys for the
      // global flip shortcut — otherwise a keyboard user can't grade a card.
      const targetEl = e.target as HTMLElement;
      if (
        keyIn(key, KEYBOARD_SHORTCUTS.flipCard) &&
        (targetEl instanceof HTMLButtonElement || targetEl.closest('button'))
      ) {
        return;
      }

      // Flip card / Next (Space or Enter)
      if (keyIn(key, KEYBOARD_SHORTCUTS.flipCard)) {
        e.preventDefault();

        if (currentMode.value === 'study') {
          flipCard();
        } else if (quizAnswered.value) {
          nextCard();
        }
        return;
      }

      // Navigation arrows
      if (keyIn(key, KEYBOARD_SHORTCUTS.nextCard)) {
        e.preventDefault();
        nextCard();
        return;
      }

      if (keyIn(key, KEYBOARD_SHORTCUTS.prevCard)) {
        e.preventDefault();
        prevCard();
        return;
      }

      // Kana toggle
      if (keyIn(key, KEYBOARD_SHORTCUTS.toggleKana)) {
        e.preventDefault();
        toggleKana();
        return;
      }

      // Study-mode grading (only after a flip). '1' = Again (incorrect),
      // '2' = Got it (correct). Guarded to study mode + flipped so these
      // never collide with quiz answer selection (quizOptions also use 1-4,
      // but only fire in quiz mode below). Each grades via gradeStudyCard,
      // which advances to the next card.
      if (currentMode.value === 'study' && isFlipped.value) {
        if (keyIn(key, KEYBOARD_SHORTCUTS.gradeAgain)) {
          e.preventDefault();
          gradeStudyCard(false);
          return;
        }
        if (keyIn(key, KEYBOARD_SHORTCUTS.gradeGotIt)) {
          e.preventDefault();
          gradeStudyCard(true);
          return;
        }
      }

      // Quiz option shortcuts (1-4)
      if (
        currentMode.value === 'quiz' &&
        !quizAnswered.value &&
        keyIn(key, KEYBOARD_SHORTCUTS.quizOptions) &&
        onQuizAnswer
      ) {
        e.preventDefault();
        const index = parseInt(key, 10) - 1;
        onQuizAnswer(index);
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, onQuizAnswer]);
}
